import { registerPayment } from "../components/PPV";
import { ContentType } from "../constants/ContentType";
import { isAuth, handleMobileAuthParams } from "./authHelper";

let templateConfigCache = null;
let templateConfigFetchInitiated = false;

/**
 * Get URL parameters for mobile app redirect handling
 * Supported parameters:
 * - paymentType: 'ppv' or 'subscription' - skip choice modal
 * - ppvId: specific PPV package UUID - go directly to that PPV payment
 * - planId: specific subscription plan UUID - pre-select that plan
 * - logout: 'true' - clear authentication before loading payment form
 * - deviceType: device platform (e.g., 'ios', 'android') - for auto-login
 * - deviceId: unique device identifier - for auto-login
 * - subscriberId: subscriber UUID - for verification with device
 */
function getPaymentUrlParams() {
  if (typeof window === "undefined") return {};
  const urlSearchParams = new URLSearchParams(window.location.search);
  return {
    paymentType: urlSearchParams.get('paymentType'),
    ppvId: urlSearchParams.get('ppvId'),
    planId: urlSearchParams.get('planId'),
    logout: urlSearchParams.get('logout'),
    deviceType: urlSearchParams.get('deviceType'),
    deviceId: urlSearchParams.get('deviceId'),
    subscriberId: urlSearchParams.get('subscriberId'),
  };
}

/**
 * Check if mobile app redirect parameters are present
 * Used to determine if payment form should auto-popup
 */
export function hasMobileRedirectParams() {
  const params = getPaymentUrlParams();
  return !!(params.paymentType || params.ppvId || params.planId);
}

function ensureTemplateConfigLoaded() {
  if (templateConfigFetchInitiated || typeof window === "undefined" || typeof fetch !== "function") {
    return;
  }
  templateConfigFetchInitiated = true;
  fetch("/template-config.json")
    .then((res) => (res.ok ? res.json() : null))
    .then((cfg) => {
      if (cfg) {
        templateConfigCache = cfg;
        if (!window.templateConfig) {
          window.templateConfig = cfg;
        }
        if (cfg.primary_color && !window.templatePrimaryColor) {
          window.templatePrimaryColor = cfg.primary_color;
        }
      }
    })
    .catch(() => {
      templateConfigCache = templateConfigCache || null;
    });
}

ensureTemplateConfigLoaded();

function getContentTypeLabel(content_type) {
  switch (content_type) {
    case ContentType.VIDEO:
      return 'video';
    case ContentType.VOD:
      return 'video category';
    case ContentType.AUDIO:
      return 'audio';
    case ContentType.AOD:
      return 'audio category';
    default:
      return 'content';
  }
}

function extractColor(value) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
}

function getTemplatePrimaryColor() {
  const sources = [
    templateConfigCache?.primary_color,
    templateConfigCache?.primaryColor,
    window?.templateConfig?.primary_color,
    window?.templateConfig?.primaryColor,
    window?.templatePrimaryColor,
    window?.templateSettings?.buttonColor,
    window?.templateSettings?.button_color,
    window?.templateSettings?.primaryColor,
    window?.templateSettings?.primary_color,
    typeof document !== "undefined"
      ? document.documentElement?.style?.getPropertyValue?.("--primary-color")
      : null,
    typeof window !== "undefined" && typeof document !== "undefined" && typeof getComputedStyle === "function"
      ? getComputedStyle(document.documentElement).getPropertyValue("--primary-color")
      : null,
  ];

  for (const source of sources) {
    const color = extractColor(source);
    if (color) {
      return color;
    }
  }
  return "#000";
}

function getUniqueSubscriptionPlans(packages = []) {
  const planMap = new Map();
  packages.forEach((pkg = {}) => {
    (pkg.subscription_plans || []).forEach((plan) => {
      if (plan && plan.uuid && !planMap.has(plan.uuid)) {
        planMap.set(plan.uuid, plan);
      }
    });
  });
  return Array.from(planMap.values());
}

export const setupPPV = async (
  baseUrl,
  apiKey,
  data,
  container,
  content_type,
  category,
  isSingle = false,
) => {
  // Handle mobile app session management (logout/subscriberId/loginToken params)
  // Extract tenant ID from API response or settings
  const tenantId = window.templateConfig?.tenantId || window.AppConfig?.tenantId;
  await handleMobileAuthParams(baseUrl, apiKey, tenantId);

  if(content_type == ContentType.PPV_EVENT || content_type == ContentType.CHANNEL || content_type == ContentType.TENANT){
    let is_sub = (content_type == ContentType.CHANNEL || content_type == ContentType.TENANT);
    
    // Handle mobile app redirect for Entire Subscription with specific planId
    const urlParams = getPaymentUrlParams();
    if (urlParams.planId && is_sub && Array.isArray(data) && data.length > 0) {
      // Find the specific plan from the mobile app
      const selectedPlan = data.find(plan => plan.uuid === urlParams.planId);
      if (selectedPlan) {
        // Route directly to the specific plan payment form
        registerPayment(baseUrl, apiKey, [selectedPlan], container, true);
        return;
      }
    }
    
    registerPayment(baseUrl, apiKey, data, container, is_sub);
  }
  else{
    // Support for multiple packages (PPV and subscription)
    let ppvPackages = [];
    let subscriptionPackages = [];
    let subscriptionCount = 0;
    let title;
    
    if (content_type == ContentType.AUDIO || content_type == ContentType.VIDEO || isSingle){
      title = data.title;
      
      // Separate PPV and subscription packages
      if (data.packages && data.packages.length > 0) {
        data.packages.forEach((pkg) => {
          if (pkg.content_type == content_type) {
            if (pkg.subscription_plans && pkg.subscription_plans.length > 0) {
              subscriptionPackages.push(pkg);
            } else {
              ppvPackages.push(pkg);
            }
          }
        });
      }
    }
    else{
      title = category.title;
      
      // Separate PPV and subscription packages from category
      if (category.packages && category.packages.length > 0) {
        category.packages.forEach((pkg) => {
          if ((pkg.content_type == ContentType.VOD || pkg.content_type == ContentType.AOD) &&
              category.related_ids.includes(pkg.content_uuid)) {
            if (pkg.subscription_plans && pkg.subscription_plans.length > 0) {
              subscriptionPackages.push(pkg);
            } else {
              ppvPackages.push(pkg);
            }
          }
        });
      }
    }

    subscriptionCount = getUniqueSubscriptionPlans(subscriptionPackages).length;

    const typeLabel = getContentTypeLabel(content_type);

    // Show payment options based on what's available
    if (ppvPackages.length > 0 || subscriptionPackages.length > 0) {
      showPaymentOptions(baseUrl, apiKey, ppvPackages, subscriptionPackages, subscriptionCount, title, container, typeLabel);
    }
  }
};

function renderModal(modalId, innerHtml) {
  $(`#${modalId}`).remove();
  const modalMarkup = `
    <div class="modal" id="${modalId}" style="display:flex;align-items:center;justify-content:center;">
      ${innerHtml}
    </div>
  `;
  $('body').append(modalMarkup);
  return $(`#${modalId}`);
}

function closeModal(modalId) {
  $(`#${modalId}`).remove();
}

/**
 * Handle payment flow when redirected from mobile app with pre-selected options
 * Returns true if handled, false if should fall through to normal flow
 */
function handleMobileRedirectPayment(baseUrl, apiKey, ppvPackages, subscriptionPackages, title, container, typeLabel, urlParams) {
  const { paymentType, ppvId, planId } = urlParams;
  
  // Handle specific PPV UUID from mobile app
  if (ppvId && ppvPackages.length > 0) {
    const selectedPkg = ppvPackages.find(pkg => pkg.uuid === ppvId);
    if (selectedPkg) {
      const data_obj = {
        'uuid': selectedPkg.uuid,
        'title': title,
        'price': selectedPkg.price,
        'price_currency': selectedPkg.price_currency,
        'content_type': selectedPkg.content_type,
      };
      registerPayment(baseUrl, apiKey, data_obj, container, false);
      return true;
    }
  }
  
  // Handle specific subscription plan UUID from mobile app
  if (planId && subscriptionPackages.length > 0) {
    const allPlans = getUniqueSubscriptionPlans(subscriptionPackages);
    const selectedPlan = allPlans.find(plan => plan.uuid === planId);
    if (selectedPlan) {
      // Pass only the selected plan to pre-select it
      registerPayment(baseUrl, apiKey, [selectedPlan], container, true);
      return true;
    }
  }
  
  // Handle paymentType=ppv from mobile app
  if (paymentType === 'ppv' && ppvPackages.length > 0) {
    if (ppvPackages.length === 1) {
      // Single PPV option, go directly to payment
      const pkg = ppvPackages[0];
      const data_obj = {
        'uuid': pkg.uuid,
        'title': title,
        'price': pkg.price,
        'price_currency': pkg.price_currency,
        'content_type': pkg.content_type,
      };
      registerPayment(baseUrl, apiKey, data_obj, container, false);
    } else {
      // Multiple PPV options, show selection but no back button to choice
      showMultiplePPVOptions(baseUrl, apiKey, ppvPackages, title, container, undefined, typeLabel, true);
    }
    return true;
  }
  
  // Handle paymentType=subscription from mobile app
  if (paymentType === 'subscription' && subscriptionPackages.length > 0) {
    const allPlans = getUniqueSubscriptionPlans(subscriptionPackages);
    registerPayment(baseUrl, apiKey, allPlans, container, true);
    return true;
  }
  
  // Not handled by mobile redirect params
  return false;
}

function showPaymentOptions(baseUrl, apiKey, ppvPackages, subscriptionPackages, subscriptionCount, title, container, typeLabel = 'content') {
  // Check URL parameters for mobile app redirect (pre-selected payment type)
  const urlParams = getPaymentUrlParams();
  
  // If mobile app has pre-selected a payment type, skip the choice modal
  if (urlParams.paymentType || urlParams.ppvId || urlParams.planId) {
    const handled = handleMobileRedirectPayment(
      baseUrl, apiKey, ppvPackages, subscriptionPackages, title, container, typeLabel, urlParams
    );
    if (handled) return;
  }
  
  // If both PPV and subscription options exist, show choice
  if (ppvPackages.length > 0 && subscriptionPackages.length > 0) {
    // Create a choice UI for user to select PPV or Subscription
    const buttonColor = getTemplatePrimaryColor();
    const choiceHtml = `
      <style>
        .modal-login-prompt {
          margin-top: 24px;
          font-size: 16px;
          color: #000000;
        }
        .modal-login-link {
          color: #000000;
          font-weight: bold;
          text-decoration: none;
        }
        .form-spacer {
          margin-bottom: 24px;
        }
      </style>
      <div class="payment-choice-modal" style="position:relative; max-width: 480px; width:100%; background:#fff; border-radius:10px; padding:20px 30px; text-align:center; box-shadow:0 10px 30px rgba(0,0,0,0.15);">
        <span class="close close-payment-choice" style="position:absolute; top:15px; right:15px; color:#000; width:32px; height:32px; display:flex; align-items:center; justify-content:center; font-size:28px; cursor:pointer;">&times;</span>
        <h2 style="color: #6f6f6f; font-size: 18px; margin-top: 20px; margin-bottom: 10px; line-height: 1.4; font-weight: 500;">
          Choose payment method for ${typeLabel}
        </h2>
        <div style="font-size: 26px; font-weight: 600; color: #1a1a1a; margin-bottom: 40px;">${title}</div>
        <div class="payment-options" style="display: flex; flex-direction: column; gap: 16px; align-items: stretch; margin-top: 20px;">
          <button class="ppv-option-btn" style="padding: 15px 30px; background: ${buttonColor}; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px; width: 100%;">
            Pay-Per-View (${ppvPackages.length} option${ppvPackages.length > 1 ? 's' : ''})
          </button>
          <button class="subscription-option-btn" style="padding: 15px 30px; background: ${buttonColor}; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px; width: 100%;">
            Subscribe to a Plan (${subscriptionCount} plan${subscriptionCount > 1 ? 's' : ''})
          </button>
        </div>
        ${!isAuth() ? `<p class="modal-login-prompt">
          Already have an account?
          <a href="/sign-in" class="modal-login-link">Login</a>
        </p>` : '<div class="form-spacer"></div>'}
      </div>
    `;

    const modal = renderModal('paymentChoiceModal', choiceHtml);

    const closeChoiceModal = () => closeModal('paymentChoiceModal');
    modal.find('.close-payment-choice').on('click', closeChoiceModal);

    // Handle PPV option selection
    modal.find('.ppv-option-btn').on('click', function() {
      if (ppvPackages.length === 1) {
        // Single PPV option, show payment directly
        const pkg = ppvPackages[0];
        const data_obj = {
          'uuid': pkg.uuid,
          'title': title,
          'price': pkg.price,
          'price_currency': pkg.price_currency,
          'content_type': pkg.content_type,
        };
        closeChoiceModal();
        registerPayment(baseUrl, apiKey, data_obj, container, false);
      } else {
        // Multiple PPV options, show selection
        closeChoiceModal();
        showMultiplePPVOptions(baseUrl, apiKey, ppvPackages, title, container, () => {
          showPaymentOptions(baseUrl, apiKey, ppvPackages, subscriptionPackages, subscriptionCount, title, container, typeLabel);
        }, typeLabel);
      }
    });
    
    // Handle subscription option selection
    modal.find('.subscription-option-btn').on('click', function() {
      const allPlans = getUniqueSubscriptionPlans(subscriptionPackages);
      closeChoiceModal();
      registerPayment(baseUrl, apiKey, allPlans, container, true);
    });
  }
  // If only PPV options exist
  else if (ppvPackages.length > 0) {
    if (ppvPackages.length === 1) {
      const pkg = ppvPackages[0];
      const data_obj = {
        'uuid': pkg.uuid,
        'title': title,
        'price': pkg.price,
        'price_currency': pkg.price_currency,
        'content_type': pkg.content_type,
      };
      registerPayment(baseUrl, apiKey, data_obj, container, false);
    } else {
      const hideBackButton = subscriptionPackages.length === 0;
      showMultiplePPVOptions(baseUrl, apiKey, ppvPackages, title, container, undefined, typeLabel, hideBackButton);
    }
  }
  // If only subscription options exist
  else if (subscriptionPackages.length > 0) {
    const allPlans = getUniqueSubscriptionPlans(subscriptionPackages);
    registerPayment(baseUrl, apiKey, allPlans, container, true);
  }
}

function showMultiplePPVOptions(baseUrl, apiKey, ppvPackages, title, container, onBack, typeLabel = 'content', hideBackButton = false) {
  // Show multiple PPV options for user to choose from
  const backButtonColor = getTemplatePrimaryColor();
  let optionsHtml = `
    <style>
      .modal-login-prompt {
        margin-top: 24px;
        font-size: 16px;
        color: #000000;
      }
      .modal-login-link {
        color: #000000;
        font-weight: bold;
        text-decoration: none;
      }
      .form-spacer {
        margin-bottom: 24px;
      }
    </style>
    <div class="ppv-options-modal" style="position:relative; max-width: 480px; width:100%; background:#fff; border-radius:10px; padding:20px 30px; text-align:center; box-shadow:0 10px 30px rgba(0,0,0,0.15);">
      <span class="close close-ppv-options" style="position:absolute; top:15px; right:15px; color:#000; width:32px; height:32px; display:flex; align-items:center; justify-content:center; font-size:28px; cursor:pointer;">&times;</span>
      <h2 style="color: #7a7a7a; font-size: 18px; margin-top: 20px; margin-bottom: 10px; line-height: 1.4; font-weight: 500;">
        Select pay-per-view option for ${typeLabel}
      </h2>
      <div style="font-size: 26px; font-weight: 600; color: #1a1a1a; margin-bottom: 20px;">${title}</div>
      <div class="ppv-options-list" style="display: flex; flex-direction: column; gap: 12px; margin-top: 20px; max-height: 320px; overflow-y: auto; text-align:left;">
  `;
  
  ppvPackages.forEach((pkg, index) => {
    const expiresInRaw = (pkg.expires_in || '').toLowerCase();
    const isLifetime = pkg.is_lifetime ||
      !expiresInRaw ||
      expiresInRaw.includes('lifetime') ||
      /\b(\d+)\s*(year|yr)/.test(expiresInRaw) && parseInt(/\b(\d+)\s*(year|yr)/.exec(expiresInRaw)[1], 10) > 10;
    const expiresText = isLifetime ? 'Lifetime access' : `Access for ${pkg.expires_in}`;
    optionsHtml += `
      <button class="ppv-option" data-index="${index}" style="padding: 18px 20px; background: #f5f7fa; color: #1a1a1a; border: 1px solid #e1e6ef; border-radius: 6px; cursor: pointer; font-size: 16px; text-align: left; transition: background 0.2s ease;">
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <div style="font-weight: 600;">${pkg.price} ${pkg.price_currency}</div>
          <div style="font-size: 14px; color:#5f6c7b;">${expiresText}</div>
        </div>
      </button>
    `;
  });
  
  optionsHtml += `
        </div>
  `;

  if (!hideBackButton) {
    optionsHtml += `
        <button class="back-btn" style="margin-top: 24px; padding: 12px 24px; background: ${backButtonColor}; color: white; border: none; border-radius: 5px; cursor: pointer; width: 100%; font-size: 16px;">
          ← Back
        </button>
    `;
  }
  optionsHtml += `
        ${!isAuth() ? `<p class="modal-login-prompt">
          Already have an account?
          <a href="/sign-in" class="modal-login-link">Login</a>
        </p>` : '<div class="form-spacer"></div>'}
    </div>
  `;
  
  const modal = renderModal('ppvOptionsModal', optionsHtml);
  const closeOptionsModal = (shouldReturn = true) => {
    closeModal('ppvOptionsModal');
    if (shouldReturn) {
      if (typeof onBack === 'function') {
        onBack();
      }
    } else {
      closeModal('paymentChoiceModal');
    }
  };
  
  // Handle PPV option selection
  modal.find('.ppv-option').on('click', function() {
    const index = $(this).data('index');
    const pkg = ppvPackages[index];
    const data_obj = {
      'uuid': pkg.uuid,
      'title': title,
      'price': pkg.price,
      'price_currency': pkg.price_currency,
      'content_type': pkg.content_type,
    };
    closeOptionsModal(false);
    registerPayment(baseUrl, apiKey, data_obj, container, false);
  });
  
  // Handle back button
  modal.find('.back-btn').on('click', function() {
    closeOptionsModal();
  });

  modal.find('.close-ppv-options').on('click', function() {
    closeOptionsModal(false);
  });
}